/**
 * Tenant management panel for platform admins.
 * Allows creating new customer organizations and viewing existing ones.
 */

import { useState, type FormEvent } from 'react';
import DataTable, { type Column } from '@/components/common/DataTable';
import Modal from '@/components/common/Modal';
import { formatDate } from '@/utils/formatters';
import { isValidSlug, isValidEmail } from '@/utils/validators';
import type { Tenant, TenantCreateRequest } from '@/types/tenant';

interface TenantManagerProps {
  tenants: Tenant[];
  onCreateTenant: (payload: TenantCreateRequest) => void;
  onRowClick?: (tenant: Tenant) => void;
  isCreating?: boolean;
}

export default function TenantManager({ tenants, onCreateTenant, onRowClick, isCreating }: TenantManagerProps) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [tier, setTier] = useState<'free' | 'starter' | 'professional' | 'enterprise'>('free');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminName, setAdminName] = useState('');
  const [error, setError] = useState('');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Organization name is required.'); return; }
    if (!isValidSlug(slug)) { setError('Slug must be lowercase alphanumeric with hyphens (2-63 chars).'); return; }
    if (!isValidEmail(adminEmail)) { setError('Invalid admin email.'); return; }
    if (adminPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!adminName.trim()) { setError('Admin name is required.'); return; }

    onCreateTenant({
      name: name.trim(),
      slug: slug.trim(),
      subscription_tier: tier,
      admin_email: adminEmail.trim(),
      admin_password: adminPassword,
      admin_full_name: adminName.trim(),
    });
    setShowForm(false);
    resetForm();
  }

  function resetForm() {
    setName(''); setSlug(''); setTier('free'); setAdminEmail(''); setAdminPassword(''); setAdminName(''); setError('');
  }

  const columns: Column<Tenant>[] = [
    { key: 'name', header: 'Organization', sortable: true },
    { key: 'slug', header: 'Slug' },
    {
      key: 'subscription_tier',
      header: 'Tier',
      render: (item: Tenant) => (
        <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 capitalize">
          {item.subscription_tier}
        </span>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (item: Tenant) => (
        <span className={item.is_active ? 'text-green-600 font-medium text-xs' : 'text-gray-400 text-xs'}>
          {item.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (item: Tenant) => <span className="text-xs">{formatDate(item.created_at)}</span>,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-28',
      render: (item: Tenant) => (
        <button
          onClick={(e) => { e.stopPropagation(); onRowClick?.(item); }}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          Edit
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Customer Organizations</h2>
        <button onClick={() => setShowForm(true)} className="btn-primary">
          New Organization
        </button>
      </div>

      <DataTable
        columns={columns}
        data={tenants}
        rowKey={(item) => item.id}
        emptyMessage="No organizations yet."
        onRowClick={onRowClick}
      />

      <Modal isOpen={showForm} onClose={() => { setShowForm(false); resetForm(); }} title="Create Organization" size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Organization Name</label>
              <input className="input-field mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Corp" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Slug</label>
              <input className="input-field mt-1" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="acme-corp" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Subscription Tier</label>
            <select
              className="input-field mt-1"
              value={tier}
              onChange={(e) => setTier(e.target.value as typeof tier)}
            >
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>

          <hr />
          <p className="text-sm font-medium text-gray-700">Initial Admin User</p>

          <div>
            <label className="block text-sm font-medium text-gray-700">Full Name</label>
            <input className="input-field mt-1" value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Jane Doe" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input className="input-field mt-1" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="jane@acme.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <input className="input-field mt-1" type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} placeholder="Minimum 8 characters" />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={isCreating} className="btn-primary">
              {isCreating ? 'Creating...' : 'Create Organization'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
